import C from "../Constants";
import EventHelperCommon from "../scheduler-event-helper/common";
import EventHelperVessel from "../scheduler-event-helper/vessel";

/**
 * 乗船期間IDを振り直す
 * @param {Store} store
 * @param {String} resourceId
 */
const reassignManningVesselCrewIdByCrewModeAsync = async (
  store,
  resourceId
) => {
  const targetEvents = store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.resourceId === resourceId &&
      event.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL &&
      !EventHelperCommon.isSoftDeleted(event)
    );
  }, true);
  targetEvents.sort(EventHelperCommon.sortByStartDate);
  // 重複のない船舶IDを取得
  const uniqueVesselIds = new Set(
    targetEvents.map(event => event.enterprise_manning_vessel_id)
  );

  for (const vesselId of uniqueVesselIds) {
    // 同じ船舶IDのイベントを取得
    const vesselEvents = targetEvents.filter(
      event => event.enterprise_manning_vessel_id === vesselId
    );

    if (vesselEvents.length > 1) {
      const previousEvent = vesselEvents[0];
      _processReAssigin(vesselEvents.slice(1), previousEvent);
    }
  }
};

/**
 * 乗船期間IDを振り直す
 * @param {Store} store
 * @param {String} rootResourceID
 */
const reassignManningVesselCrewIdByVesselModeAsync = async (
  store,
  rootResourceID,
  crewId
) => {
  const targetEvents = store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.crew_id === crewId &&
      event.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL &&
      !EventHelperCommon.isSoftDeleted(event)
    );
  }, true);
  targetEvents.sort(EventHelperCommon.sortByStartDate);
  // 重複のない船舶IDを取得
  const uniqueRootResourceIDs = new Set(
    targetEvents.map(
      event => EventHelperVessel.getRootResource(event.resource).id
    )
  );

  for (const vesselId of uniqueRootResourceIDs) {
    // 同じ船舶IDのイベントを取得
    const vesselEvents = targetEvents.filter(
      event => EventHelperVessel.getRootResource(event.resource).id === vesselId
    );

    if (vesselEvents.length > 1) {
      const previousEvent = vesselEvents[0];
      _processReAssigin(vesselEvents.slice(1), previousEvent);
    }
  }
};

/**
 * 乗船中イベントの乗船期間IDを更新するヘルパー関数
 * @param {EventModel} targetEvents
 * @param {EventModel} previousEvent
 * @returns
 */
const _processReAssigin = (targetEvents, previousEvent) => {
  if (targetEvents.length === 0) {
    return;
  }

  // 判定対象のイベントを取得
  const currentEvent = targetEvents[0];

  // 事前に乗船期間ID保持
  const currentManningVesselCrewId =
    currentEvent.enterprise_manning_vessel_crew_id;

  // 乗船期間IDの更新が必要かを判定
  // 更新理由によって処理を分岐
  const reason = _shouldUpdateVesselCrewId(previousEvent, currentEvent);

  if (reason === UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE) {
    // 更新不要の場合は次のイベントを処理
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // 判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.PREVIOUS_STATUS_WAS_PLANNED) {
    // 前の乗船期間IDを判定対象の乗船期間IDに更新
    EventHelperCommon.changeEnterpriseManningVesselCrewId(
      previousEvent,
      currentEvent.enterprise_manning_vessel_crew_id,
      currentEvent.version
    );
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.CURRENT_STATUS_WAS_PLANNED) {
    // 判定対象の乗船期間IDを前の乗船期間IDに更新
    EventHelperCommon.changeEnterpriseManningVesselCrewId(
      currentEvent,
      previousEvent.enterprise_manning_vessel_crew_id,
      previousEvent.version
    );
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // イベントが繋がっているが、乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_CREW_ID) {
    // 判定対象の乗船期間IDと同じイベントを前のイベントの乗船期間IDに更新
    // 乗船期間IDが変わった時に再起呼び出し
    for (let i = 0; i < targetEvents.length; i++) {
      const event = targetEvents[i];
      if (
        currentManningVesselCrewId === event.enterprise_manning_vessel_crew_id
      ) {
        EventHelperCommon.changeEnterpriseManningVesselCrewId(
          event,
          previousEvent.enterprise_manning_vessel_crew_id,
          previousEvent.version
        );
      } else {
        _processReAssigin(targetEvents.slice(i + 1), event);
        break;
      }
    }
  }

  // イベント期間の繋がりがなく、乗船期間IDが同じ
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_DATE) {
    // phantomGrupKeyを生成
    const phantomGrupKey = EventHelperCommon.genPhantomGroupKey();

    // 判定対象の乗船期間IDと同じイベントの乗船期間IDをnullに更新
    // サーバサイドに乗船期間IDを発行してもらうためにphantomGroupKeyを設定
    // 乗船期間IDが変わった時に再起呼び出し
    for (let i = 0; i < targetEvents.length; i++) {
      const event = targetEvents[i];
      if (
        currentManningVesselCrewId === event.enterprise_manning_vessel_crew_id
      ) {
        EventHelperCommon.changeEnterpriseManningVesselCrewId(
          event,
          null,
          null
        );
        EventHelperCommon.setPhantomGroupKey(event, phantomGrupKey);
      } else {
        _processReAssigin(targetEvents.slice(i + 1), event);
        break;
      }
    }
  }
};

/**
 * 乗船期間IDを更新する必要がある理由を定義する定数
 * @readonly
 * @enum {number}
 * @property {number} NO_UPDATE
 * - 更新不要
 * @property {number} PREVIOUS_STATUS_WAS_PLANNED
 * - 判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
 * @property {number} CURRENT_STATUS_WAS_PLANNED
 * - 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
 * @property {number} MISSMATCH_VESSEL_CREW_ID
 * - イベントが繋がっているが、乗船期間IDが異なる
 * @property {number} MISSMATCH_VESSEL_DATE
 * - イベント期間の繋がりがなく、乗船期間IDが同じ
 * @returns {UPDATE_VESSEL_CREW_ID_REASON}
 */
const UPDATE_VESSEL_CREW_ID_REASON = {
  NO_UPDATE: 0,
  PREVIOUS_STATUS_WAS_PLANNED: 1,
  CURRENT_STATUS_WAS_PLANNED: 2,
  MISSMATCH_VESSEL_CREW_ID: 3,
  MISSMATCH_VESSEL_DATE: 4,
};

/**
 * 乗船中様態のイベントの乗船期間IDを更新する必要があるかを判定するヘルパー関数
 * @param {CrewEventModel} previousEvent
 * @param {CrewEventModel} currentEvent
 * @returns {UPDATE_VESSEL_CREW_ID_REASON}
 */
const _shouldUpdateVesselCrewId = (previousEvent, currentEvent) => {
  //判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
  if (
    EventHelperCommon.wasPlanned(previousEvent) &&
    previousEvent.endDate.getTime() === currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.PREVIOUS_STATUS_WAS_PLANNED;
  }

  // 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
  if (
    EventHelperCommon.wasPlanned(currentEvent) &&
    previousEvent.endDate.getTime() === currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.CURRENT_STATUS_WAS_PLANNED;
  }

  // 上記以外の元計画中イベントは更新不要
  if (
    EventHelperCommon.wasPlanned(previousEvent) ||
    EventHelperCommon.wasPlanned(currentEvent)
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE;
  }

  // イベントが繋がっているが、乗船期間IDが異なる
  if (
    previousEvent.endDate.getTime() === currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_CREW_ID;
  }

  // イベント期間の繋がりがなく、乗船期間IDが同じ
  if (
    previousEvent.endDate.getTime() !== currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id ===
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_DATE;
  }

  return UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE;
};

export {
  reassignManningVesselCrewIdByCrewModeAsync,
  reassignManningVesselCrewIdByVesselModeAsync,
};
